import { useState, useCallback, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useSearchParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import Header from './components/Header';
import Hero from './components/Hero';
import Features from './components/Features';
import Pricing from './components/Pricing';
import Footer from './components/Footer';
import AuthModal from './components/AuthModal';
import Account from './components/Account';
import PaymentSuccess from './components/PaymentSuccess';
import WorkspacePage from './components/WorkspacePage';
import WorkshopPage from './components/WorkshopPage';

const API = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

function LandingPage({ onAuthClick }) {
  const { user, getAccessToken } = useAuth();

  const handleCheckout = useCallback(async (packageId) => {
    try {
      if (!API) {
        throw new Error('REACT_APP_BACKEND_URL is not configured.');
      }
      if (!user) {
        onAuthClick('register');
        return;
      }

      const token = await getAccessToken();
      const res = await fetch(`${API}/api/checkout/create-session`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ package_id: packageId }),
      });

      if (!res.ok) {
        const errorBody = await res.json().catch(() => ({}));
        throw new Error(errorBody.detail || 'Failed to create checkout.');
      }

      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      console.error('Checkout error:', err);
      window.alert(err.message || 'Unable to start checkout.');
    }
  }, [getAccessToken, onAuthClick, user]);

  return (
    <>
      <Hero onGetStarted={() => onAuthClick('register')} />
      <Features />
      <Pricing onCheckout={handleCheckout} onAuthClick={onAuthClick} />
      <Footer />
    </>
  );
}

function AppContent() {
  const [authModal, setAuthModal] = useState(null);
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const isWorkspace = location.pathname === '/workspace' || location.pathname === '/workshop';

  useEffect(() => {
    const authParam = searchParams.get('auth');
    if (authParam === 'login' || authParam === 'register') {
      setAuthModal(authParam);
      searchParams.delete('auth');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const openAuth = (mode) => setAuthModal(mode);
  const closeAuth = () => setAuthModal(null);

  return (
    <AuthProvider>
      <div className="min-h-screen bg-bg text-white" data-testid="app-root">
        {!isWorkspace && <Header onAuthClick={openAuth} />}
        <Routes>
          <Route path="/" element={<LandingPage onAuthClick={openAuth} />} />
          <Route path="/workspace" element={<WorkspacePage />} />
          <Route path="/workshop" element={<WorkshopPage />} />
          <Route path="/account" element={<Account />} />
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route path="/pricing" element={<LandingPage onAuthClick={openAuth} />} />
        </Routes>
        {authModal && <AuthModal mode={authModal} onClose={closeAuth} />}
      </div>
    </AuthProvider>
  );
}

export default function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}
